import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { Github, Linkedin, Send, Check } from 'lucide-react';
import { useAuth } from '~/hooks/useAuth';
import { formatDuration } from '~/data/courseData';
import { publishedCourses } from '~/data/courses';
import { getConfig } from '~/lib/config.server';
import type { Route } from './+types/_index';
import type { AppEnv } from '~/lib/env.server';

export const links: Route.LinksFunction = () => [];

// Only publicly listed courses get a card; the registry flag is the
// landing-page listing signal (see app/data/courses.ts for how it
// differs from the DB is_published content flag).
const courses = publishedCourses();

const LINKEDIN = 'https://www.linkedin.com/in/foyzul';
const GITHUB = 'https://github.com/foyzulkarim';
const TELEGRAM = 'https://t.me/foyzulk';

const principles = [
  {
    title: 'Specify before you delegate',
    desc: 'Requirements, architecture, and tasks make agent work bounded and repeatable.',
  },
  {
    title: 'Make verification explicit',
    desc: 'Tests, reviews, runtime signals, and other oracles turn “it works” into evidence.',
  },
  {
    title: 'Keep the loop closed',
    desc: 'What happens after shipping becomes input to the next decision instead of disappearing.',
  },
];

export async function loader({ context }: Route.LoaderArgs) {
  const env = context.cloudflare.env as AppEnv;
  // R30/R43: displayed prices come from config, never courses.price_cents.
  const { PRICE_USD_MINOR, PRICE_BDT_MINOR, TRIAL_DAYS, PAID_MONTH_DAYS } = getConfig(env);
  return {
    priceUsdMinor: PRICE_USD_MINOR,
    priceBdtMinor: PRICE_BDT_MINOR,
    trialDays: TRIAL_DAYS,
    paidMonthDays: PAID_MONTH_DAYS,
  };
}

export default function Home({ loaderData }: Route.ComponentProps) {
  const { priceUsdMinor, priceBdtMinor, trialDays, paidMonthDays } = loaderData;
  const priceUSD = priceUsdMinor / 100;
  const priceBDT = priceBdtMinor / 100;
  const priceMonths = Math.floor(paidMonthDays / 30);
  const periodLabel = `${priceMonths} month${priceMonths === 1 ? '' : 's'}`;
  const { isLoggedIn } = useAuth();
  const [searchParams] = useSearchParams();

  /* No-access toast.
     The global <Toaster> (in Layout) subscribes to sonner only in a passive
     effect, and empirically a toast published during the initial effect
     flush is dropped (sonner only notifies current subscribers, no replay).
     Wait for the toaster region to exist before firing, with a hard
     fallback so the toast still fires if the marker never appears. */
  useEffect(() => {
    if (searchParams.get('reason') !== 'no_access') return;
    let attempts = 0;
    let t: number | undefined;
    const fire = () => {
      if (document.querySelector('section[aria-label="Notifications"]') || ++attempts > 30) {
        toast.error('Get access to continue', {
          description: 'Start your free trial or sign in to unlock all video lessons and track your progress.',
          duration: 6000,
        });
      } else {
        t = window.setTimeout(fire, 100);
      }
    };
    t = window.setTimeout(fire, 0);
    return () => window.clearTimeout(t);
  }, [searchParams]);

  return (
    <>
      {/* ============================================================ */}
      {/* SECTION 1 — HERO                                             */}
      {/* ============================================================ */}
      <section className="relative min-h-[85dvh] flex items-center bg-bg-primary overflow-hidden">
        {/* Gradient halo */}
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(34,211,238,0.06) 0%, transparent 70%)',
          }}
        />

        <div className="max-w-content mx-auto px-6 lg:px-10 py-24 pt-32 w-full relative">
          <p className="text-section-label mb-4">For software engineers</p>

          <h1 className="font-heading text-hero font-medium text-text-primary tracking-tight mb-6">
            Build software with AI.
            <br />
            Engineer the process.
          </h1>

          <p className="font-body text-body text-text-secondary max-w-[640px] mb-8">
            Practical courses for engineers who want to move beyond one-shot
            prompting and learn how agents, skills, verification, and the
            underlying mechanics fit into a real engineering workflow.
          </p>

          <div className="flex flex-wrap gap-3">
            <a
              href="#courses"
              className="inline-block font-heading text-sm font-medium bg-accent-cyan text-bg-primary px-6 py-3 rounded-md hover:bg-accent-hover hover:scale-[1.03] hover:shadow-glow transition-all duration-200 no-underline"
            >
              Explore the courses
            </a>
            <a
              href="#approach"
              className="inline-block font-heading text-sm font-medium border border-border-active text-text-primary px-6 py-3 rounded-md hover:border-accent-cyan hover:text-accent-cyan transition-all duration-200 no-underline"
            >
              See the approach
            </a>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 2 — COURSES                                          */}
      {/* ============================================================ */}
      <section
        id="courses"
        className="scroll-mt-16 bg-bg-primary border-t border-border-subtle py-24 lg:py-32"
      >
        <div className="max-w-content mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <p className="text-section-label mb-4">COURSES</p>
              <h2 className="font-heading text-h2 text-text-primary">
                Many courses. One direction.
              </h2>
            </div>
            <p className="font-body text-body text-text-secondary max-w-[510px]">
              Start with the mechanics and tools, then go deeper into the
              engineering process that turns agent output into software you can
              actually reason about, verify, and ship.
            </p>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {courses.map((entry, i) => {
              const isFeatured = entry.course.slug === 'the-control-loop';
              return (
                <li key={entry.course.slug} className="h-full">
                  <article
                    className={`flex flex-col h-full bg-bg-elevated rounded-xl overflow-hidden transition-all duration-200 hover:border-border-active hover:-translate-y-1 hover:shadow-card-hover ${
                      isFeatured ? 'border border-accent-cyan/40' : 'border border-border-subtle'
                    }`}
                  >
                    <div className="aspect-video bg-bg-surface relative">
                      <img
                        src={entry.course.thumbnailUrl}
                        alt=""
                        width={1376}
                        height={768}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <p className="font-heading text-xs font-medium uppercase tracking-wider text-accent-cyan mb-3">
                        Course {String(i + 1).padStart(2, '0')}
                      </p>
                      <h3 className="font-heading text-h3 text-text-primary mb-2">
                        {entry.course.title}
                      </h3>
                      <p className="font-body text-sm text-text-secondary mb-4">
                        {entry.course.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-6">
                        <span className="font-body text-xs text-text-secondary bg-bg-surface border border-border-subtle rounded px-2 py-1">
                          {entry.modules.length} modules
                        </span>
                        <span className="font-body text-xs text-text-secondary bg-bg-surface border border-border-subtle rounded px-2 py-1">
                          {entry.totalLessons} lessons
                        </span>
                        {entry.totalMinutes > 0 && (
                          <span className="font-body text-xs text-text-secondary bg-bg-surface border border-border-subtle rounded px-2 py-1">
                            ~{formatDuration(entry.totalMinutes)}
                          </span>
                        )}
                      </div>
                      <div className="mt-auto">
                        <Link
                          to={`/course/${entry.course.slug}`}
                          aria-label={`View course: ${entry.course.title}`}
                          className="inline-block font-heading text-sm font-medium bg-accent-cyan text-bg-primary px-5 py-2.5 rounded-md hover:bg-accent-hover transition-colors duration-200 no-underline"
                        >
                          View course &rarr;
                        </Link>
                        {/* dbIsPublished === 0 means course content isn't
                            launched yet; the badge disappears on its own
                            when the registry flag flips. */}
                        {entry.dbIsPublished === 0 && (
                          <div className="inline-flex items-center gap-2 border border-warning/30 rounded-full px-3 py-1 ml-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-warning animate-pulse shrink-0" />
                            <span className="font-body text-xs text-warning">
                              Currently being developed
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 3 — APPROACH                                         */}
      {/* ============================================================ */}
      <section
        id="approach"
        className="scroll-mt-16 bg-bg-primary border-t border-border-subtle py-24 lg:py-32"
      >
        <div className="max-w-content mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <p className="text-section-label mb-4">THE APPROACH</p>
              <h2 className="font-heading text-h2 text-text-primary">
                From prompting to engineering.
              </h2>
            </div>
            <p className="font-body text-body text-text-secondary max-w-[510px]">
              The courses share a simple premise: a working output is not
              enough. The engineer still owns the specification, decisions,
              verification, constraints, and evidence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {principles.map((principle) => (
              <div key={principle.title} className="border-t-2 border-accent-cyan pt-5">
                <h3 className="font-heading text-h4 text-text-primary mb-2">
                  {principle.title}
                </h3>
                <p className="font-body text-sm text-text-secondary">
                  {principle.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 4 — AUDIENCE QUALIFIER                               */}
      {/* ============================================================ */}
      <section
        id="about"
        className="scroll-mt-16 bg-bg-primary border-t border-border-subtle py-24 lg:py-32"
      >
        <div className="max-w-content mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.6fr] gap-10 lg:gap-16 items-start">
            <h2 className="font-heading text-h2 text-text-primary">
              For people who already know how to code.
            </h2>
            <div className="space-y-4">
              <p className="font-body text-body text-text-secondary">
                This is not a beginner programming curriculum. The focus is the
                changing boundary between software engineering judgment and AI
                execution.
              </p>
              <p className="font-body text-body text-text-secondary">
                The foundation course covers agents, Claude Code, skills, and
                inference mechanics. The Control Loop then follows a real build
                and makes the engineering process visible: discovery, decisions,
                planning, implementation, verification, shipping, and feedback.
              </p>
              <p className="font-body text-body text-text-secondary">
                The goal is not to teach a collection of prompts. It is to
                develop a way of working that remains useful as models, tools,
                and interfaces change.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 5 — INSTRUCTOR STRIP                                 */}
      {/* ============================================================ */}
      <section className="bg-bg-primary border-t border-border-subtle py-16 lg:py-20">
        <div className="max-w-content mx-auto px-6 lg:px-10">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <img
              src="https://assets.foyzul.com/aswe-lms/author-photo-320.webp"
              alt="Foyzul Karim"
              loading="lazy"
              decoding="async"
              className="w-16 h-16 rounded-full object-cover border border-border-subtle shrink-0"
            />
            <div>
              <p className="text-section-label mb-2">INSTRUCTOR</p>
              <p className="font-heading text-h4 text-text-primary mb-2">
                Foyzul Karim
              </p>
              <p className="font-body text-sm text-text-secondary max-w-[640px] mb-4">
                Senior Software Engineer with over 15 years of experience
                building high-performance, scalable systems with Node.js,
                React, TypeScript, and AWS — now focused on the engineering
                discipline behind building software with AI agents.
              </p>
              <div className="flex items-center gap-4">
                <a
                  href={GITHUB}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-text-secondary hover:text-accent-cyan hover:scale-[1.15] transition-all duration-150"
                  aria-label="GitHub"
                >
                  <Github size={20} />
                </a>
                <a
                  href={LINKEDIN}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-text-secondary hover:text-accent-cyan hover:scale-[1.15] transition-all duration-150"
                  aria-label="LinkedIn"
                >
                  <Linkedin size={20} />
                </a>
                <a
                  href={TELEGRAM}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-text-secondary hover:text-accent-cyan hover:scale-[1.15] transition-all duration-150"
                  aria-label="Telegram"
                >
                  <Send size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 6 — PRICING / CTA                                    */}
      {/* ============================================================ */}
      <section id="pricing" className="scroll-mt-16 section-pricing bg-bg-primary py-24 lg:py-32 relative overflow-hidden border-t border-border-subtle">
        {/* Subtle glow */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(34,211,238,0.04) 0%, transparent 70%)',
          }}
        />

        <div className="max-w-content mx-auto px-6 lg:px-10 relative">
          <div className="text-center mb-12">
            <h2 className="font-heading text-h2 text-text-primary mb-4">
              Process beats prompts.
            </h2>
            <p className="font-body text-body text-text-secondary max-w-[480px] mx-auto">
              One subscription, every course included. Start with a free{' '}
              {trialDays}-day trial, then ${priceUSD} / {periodLabel} — pay again
              every {periodLabel} to keep access. Nothing renews automatically.
            </p>
          </div>

          {/* Price Card */}
          <div className="max-w-[400px] mx-auto">
            <div className="bg-bg-elevated border border-border-subtle rounded-xl p-10 hover:border-border-active hover:-translate-y-1 hover:shadow-glow-lg transition-all duration-300">
              <div className="flex items-baseline justify-center gap-2 mb-6">
                <span className="font-heading text-hero text-text-primary">
                  ${priceUSD}
                </span>
                <span className="font-body text-sm text-text-secondary">USD / {periodLabel}</span>
              </div>

              <div className="border-t border-border-subtle pt-6 mb-6">
                <ul className="space-y-3">
                  {[
                    'Every video lesson, every course',
                    'Production-grade patterns, not toy demos',
                    'Every course included',
                    `Free ${trialDays}-day trial, no card required`,
                    'New courses added at no extra cost',
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Check size={16} className="text-success shrink-0" />
                      <span className="font-body text-sm text-text-secondary">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {isLoggedIn ? (
                <Link
                  to="/dashboard"
                  className="block w-full text-center font-heading text-sm font-medium bg-accent-cyan text-bg-primary px-6 py-3 rounded-md hover:bg-accent-hover hover:scale-[1.03] hover:shadow-glow transition-all duration-200 no-underline"
                >
                  Go to Dashboard
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="block w-full text-center font-heading text-sm font-medium bg-accent-cyan text-bg-primary px-6 py-3 rounded-md hover:bg-accent-hover hover:scale-[1.03] hover:shadow-glow transition-all duration-200 no-underline"
                >
                  Start free trial
                </Link>
              )}

            </div>

            <div className="flex items-center justify-center gap-6 mt-6">
              <span className="font-body text-xs text-accent-cyan">
                No card stored — nothing renews automatically
              </span>
              <span className="font-body text-xs text-accent-cyan">
                In Bangladesh? Pay via bKash/Nagad/bank — ৳{priceBDT} / {periodLabel}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* FOOTER LINE                                                  */}
      {/* ============================================================ */}
      <footer className="bg-bg-primary border-t border-border-subtle">
        <div className="max-w-content mx-auto px-6 lg:px-10 py-8">
          <p className="font-body text-xs text-text-muted text-center">
            &copy; 2026 ASE — Agentic Software Engineering. All rights reserved.
          </p>
        </div>
      </footer>
    </>
  );
}
